import { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Badge, Divider, Grid, IconButton, Paper } from "@mui/material"
import classnames from "classnames"
import Tooltip from "components/material/tooltip"
import { CenterFocusStrong, CenterFocusWeak, FilterList } from "@mui/icons-material"
import { map } from "lodash"
import Filters from "components/analyze-header-filters"
import ExpansionMenu from "components/expansion-menu"
import GroupingMenu from "components/grouping-menu"
import PeriodSelector from "components/period-selector"
import SelectedDate from "components/selected-date"
import { NORMALIZATION } from "helpers/segments"
import { DATE, FILTER_ON_GROUP, OPTIONS_TOGGLE_ITEM, PERIOD, OPTIONS_PAGE_SELECTOR, PERIOD_SELECTOR_LABELS, FILTER } from "helpers/shapes"
import OptionsToggle from "components/options-toggle"
import IntradayRefresh from "components/intraday/refresh"

import "./styles.scss"

export default function AssetsHeader({
  hasData,
  isActive,
  isRefreshing,
  lastRefresh,
  availableRollups,
  currentGrouping,
  setGrouping,
  isExpanded,
  hasUnexpandedComposites,
  setExpansionMode,
  filtersVisible,
  toggleFilters,
  account,
  date,
  filterOns,
  modelId,
  normalization,
  disableNormalization,
  disableNormalizationTooltip,
  isNormalizationAdjusted,
  assetId,
  assetType,
  setNormalization,
  title,
  isIntraday,
  periodAdjusted,
  selectedDateAdjusted,
  earliestDate,
  latestDate,
  firstDate,
  startDate,
  endDate,
  period,
  setPeriod,
  datesWithLabels,
  selectedDate,
  resetDate,
  addNotification,
  showNotHeld,
  toggleShowNotHeld,
  screens,
  selectedScreen,
  selectScreen,
  pages,
  selectedPage,
  selectPage,
  customSelectScreen,
  customDateSelector,
  periodSelectorLabels,
  hasUnsupportedContributionFilter,
  currency,
  customLeftButtons,
  addedFilters,
  updateFilters,
  isPortfolioFiltersLoading,
  isHasUnexpandedCompositesLoading,
}) {
  const filterButtonRef = useRef(null)

  useEffect(() => {
    if (isNormalizationAdjusted) {
      addNotification("warning", null, "Segment normalization has been set to Standalone. Normalization: Contribution is not supported when in active mode.")
    }
  }, [addNotification, isNormalizationAdjusted])

  const handleSetPeriod = (change) => {
    if (!change.startDate) {
      setPeriod(change)
      return
    }

    if (change.adjusted.startDate || change.adjusted.endDate) {
      const from = change.adjusted.startDate || change.startDate
      const to = change.adjusted.endDate || change.endDate
      if (addNotification) {
        addNotification("info", null, `The selected dates have been adjusted to the nearest available date range: ${from} to ${to}.`)
      }
    }

    setPeriod({
      startDate: change.adjusted.startDate || change.startDate,
      endDate: change.adjusted.endDate || change.endDate,
    })
  }

  return <div className="components_assets-header">
    <div className="toolbar">
      {customSelectScreen}
      {(selectScreen || selectPage) && !customSelectScreen && <Paper square className="left-container">
        {selectScreen && <OptionsToggle selectedValue={selectedScreen} items={screens} onSelect={selectScreen} />}
        {selectPage && <div className="page-selector">
          {map(pages, ({ value, icon, tooltip }) => {
            const button = <IconButton
              key={value}
              onClick={() => selectPage(value)}
              className={selectedPage === value ? "active-page" : ""}
              size="large"
            >
              {icon}
            </IconButton>
            if (tooltip) {
              return <Tooltip key={value} title={tooltip}>{button}</Tooltip>
            }
            return button
          })}
        </div>}
      </Paper>}
      <Paper square className="right-container">
        <div className="left-content">
          {title && <div className="title">
            {title}
          </div>}
          {isIntraday && <IntradayRefresh {...{ hasData, isRefreshing, lastRefresh }} />}
          {customDateSelector}
          {!isIntraday && !customDateSelector && <>
            <PeriodSelector {...{
              periodAdjusted,
              selectedDateAdjusted,
              earliestDate,
              latestDate,
              startDate: firstDate || startDate,
              endDate,
              period,
              onChange: handleSetPeriod,
              datesWithLabels,
              labels: periodSelectorLabels,
            }}
            />
            {selectedDate < endDate && <SelectedDate date={selectedDate} resetHandler={resetDate} />}
          </>}
          {map(customLeftButtons, (button, index) => ({ ...button, key: `customLeftButton-${index}` }))}
        </div>
        <Grid container justifyContent="flex-end" className={classnames("right-content", { disabled: isIntraday && !hasData })}>
          {setGrouping && <GroupingMenu {...{ availableRollups, setGrouping, currentGrouping }} />}
          {isActive && !isIntraday && toggleShowNotHeld && <Tooltip enterDelay={300} title={`${showNotHeld ? "Hide" : "Show"} benchmark names`}>
            <IconButton
              id="benchmark-names"
              onClick={() => toggleShowNotHeld(!showNotHeld)}
              size="large"
            >
              {showNotHeld ? <CenterFocusWeak /> : <CenterFocusStrong />}
            </IconButton>
          </Tooltip>}
          {setExpansionMode && <ExpansionMenu {...{ setExpansionMode, isExpanded, hasUnexpandedComposites, isLoading: isHasUnexpandedCompositesLoading }} />}
          <Divider orientation="vertical" flexItem />
          <Tooltip title="Filter positions">
            <IconButton
              ref={filterButtonRef}
              onClick={toggleFilters}
              className={classnames("filters-toggle", { active: filtersVisible })}
              size="large"
              loading={isPortfolioFiltersLoading}
            >
              <Badge overlap="rectangular" color="primary" invisible={filtersVisible || addedFilters?.length === 0} badgeContent={addedFilters?.length}>
                <FilterList />
              </Badge>
            </IconButton>
          </Tooltip>
        </Grid>
      </Paper>
    </div>

    {filtersVisible && (
      <Filters
        {...{
          account,
          addedFilters,
          updateFilters,
          endDate: isIntraday ? date : endDate,
          filterOns,
          heapAddFilterId: "intraday-segment-parameters",
          modelId,
          normalization,
          disableNormalization,
          disableNormalizationTooltip,
          isNormalizationAdjusted,
          assetId,
          assetType,
          setNormalization,
          startDate: isIntraday ? date : startDate,
          intraday: isIntraday,
          isExpanded,
          filtersVisible,
          hasUnsupportedContributionFilter,
          currency,
          toggleFilters,
          anchorElement: filterButtonRef.current,
        }}
      />
    )}
  </div>
}

AssetsHeader.propTypes = {
  addedFilters: PropTypes.arrayOf(FILTER),
  updateFilters: PropTypes.func,
  hasData: PropTypes.bool,
  isActive: PropTypes.bool,
  isRefreshing: PropTypes.bool,
  lastRefresh: PropTypes.string,
  availableRollups: PropTypes.array.isRequired,
  currentGrouping: PropTypes.object,
  setGrouping: PropTypes.func,
  setExpansionMode: PropTypes.func,
  isExpanded: PropTypes.bool,
  hasUnexpandedComposites: PropTypes.bool,
  filtersVisible: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  account: PropTypes.string.isRequired,
  date: DATE,

  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  modelId: PropTypes.string.isRequired,
  assetId: PropTypes.string,
  assetType: PropTypes.string,
  normalization: PropTypes.oneOf(map(NORMALIZATION)),
  disableNormalization: PropTypes.bool,
  disableNormalizationTooltip: PropTypes.string,
  isNormalizationAdjusted: PropTypes.bool,
  setNormalization: PropTypes.func.isRequired,
  title: PropTypes.string,
  isIntraday: PropTypes.bool,
  periodAdjusted: PropTypes.bool,
  selectedDateAdjusted: PropTypes.bool,
  earliestDate: DATE,
  latestDate: DATE,
  firstDate: PropTypes.string,
  startDate: DATE,
  endDate: DATE,
  period: PERIOD,
  setPeriod: PropTypes.func,
  datesWithLabels: PropTypes.bool,
  selectedDate: DATE,
  addNotification: PropTypes.func,
  resetDate: PropTypes.func,
  showNotHeld: PropTypes.bool,
  toggleShowNotHeld: PropTypes.func,
  screens: PropTypes.arrayOf(OPTIONS_TOGGLE_ITEM),
  selectedScreen: PropTypes.string,
  selectScreen: PropTypes.func,
  pages: PropTypes.arrayOf(OPTIONS_PAGE_SELECTOR),
  selectedPage: PropTypes.string,
  selectPage: PropTypes.func,
  customSelectScreen: PropTypes.node,
  customDateSelector: PropTypes.node,
  periodSelectorLabels: PERIOD_SELECTOR_LABELS,
  hasUnsupportedContributionFilter: PropTypes.bool,
  currency: PropTypes.string,
  customLeftButtons: PropTypes.arrayOf(PropTypes.node),
  isPortfolioFiltersLoading: PropTypes.bool,
  isHasUnexpandedCompositesLoading: PropTypes.bool,
}
